import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { OcApiError, ocFetch, ocSetSessionToken } from "@/lib/oc-api";

const OC_REMEMBER_KEY = "oc_remembered_email";
/** Per-account token from "Trust this device", which skips the emailed code. */
const ocTrustedDeviceKey = (email: string) => `oc_trusted_device:${email.trim().toLowerCase()}`;

interface OcLoginSession {
  token: string;
  expiresAt?: number;
  user: { email: string; name: string; role: string };
  deviceToken?: string;
}

/**
 * The second-factor step, when the tenant asks for one. "email" carries the
 * masked inbox and resend clock; "authenticator" carries neither, and on a
 * user's first sign-in after the tenant switches it on it also carries the
 * one-time pairing secret to scan.
 */
interface OcLoginCodeChallenge {
  codeRequired: true;
  factor?: "email" | "authenticator";
  challengeId: string;
  emailHint?: string;
  expiresAt: number;
  resendAvailableAt?: number;
  /** Authenticator enrolment: show the QR / key before asking for a code. */
  enroll?: boolean;
  secret?: string;
  otpauthUrl?: string;
}

const SSO_ERROR_MESSAGES: Record<string, string> = {
  tenant_not_found: "Tenant not found. Contact your administrator.",
  tenant_inactive: "Your organization's account is inactive. Contact your administrator.",
  access_locked: "Access to this organization's portal is currently turned off. Contact your OnboardConnect administrator.",
  sso_not_configured: "SSO is not configured for this organization.",
  discovery_failed: "Failed to reach the identity provider. Try again later.",
  token_exchange_failed: "Token exchange with identity provider failed. Try again.",
  no_id_token: "No identity token received from the identity provider.",
  invalid_id_token: "Identity token is invalid. Contact your administrator.",
  nonce_mismatch: "Security check failed. Please try signing in again.",
  no_email_in_token: "No email address in the identity token.",
  user_not_found: "Your account was not found in this organization. Contact your administrator.",
  missing_token: "SSO did not return a session token.",
};

export function OcLoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => localStorage.getItem(OC_REMEMBER_KEY) ?? "");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(OC_REMEMBER_KEY));
  const [error, setError] = useState<string | null>(() => {
    const ssoError = searchParams.get("sso_error");
    if (!ssoError) return null;
    return SSO_ERROR_MESSAGES[ssoError] ?? `SSO error: ${ssoError}`;
  });
  const [loading, setLoading] = useState(false);
  const impersonationEnded = searchParams.get("signed_out") === "impersonation";
  // Set by the portal's session watcher when it signs the user out, so the
  // login screen explains why they landed back here.
  const expiredReason = searchParams.get("expired");
  const expiredMessage =
    expiredReason === "inactivity"
      ? "You were signed out because your session was inactive. Please sign in again."
      : expiredReason === "absolute"
        ? "Your session reached its maximum length and ended. Please sign in again."
        : null;

  const [challenge, setChallenge] = useState<OcLoginCodeChallenge | null>(null);
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const isAuthenticator = challenge?.factor === "authenticator";

  // Ticks the "Resend code in Ns" countdown while the email code step shows.
  useEffect(() => {
    if (!challenge || challenge.factor === "authenticator") return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [challenge]);

  // The QR encoder is only needed on the one-time pairing screen, so it is
  // imported lazily and kept out of the main bundle.
  useEffect(() => {
    const url = challenge?.otpauthUrl;
    if (!url) {
      setQrDataUrl(null);
      return;
    }
    let cancelled = false;
    void import("qrcode")
      .then((qr) => qr.toDataURL(url, { margin: 1, width: 232, errorCorrectionLevel: "M" }))
      .then((dataUrl) => {
        if (!cancelled) setQrDataUrl(dataUrl);
      })
      .catch(() => {
        // The key below the QR is enough to finish pairing by hand.
        if (!cancelled) setQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [challenge?.otpauthUrl]);

  function completeLogin(res: OcLoginSession) {
    if (rememberMe) {
      localStorage.setItem(OC_REMEMBER_KEY, email);
    } else {
      localStorage.removeItem(OC_REMEMBER_KEY);
    }
    if (res.deviceToken) localStorage.setItem(ocTrustedDeviceKey(email), res.deviceToken);
    ocSetSessionToken(res.token, res.expiresAt);
    navigate("/dashboard", { replace: true });
  }

  function backToPassword(message: string | null) {
    setChallenge(null);
    setQrDataUrl(null);
    setCode("");
    setPassword("");
    setNotice(null);
    setError(message);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const deviceToken = localStorage.getItem(ocTrustedDeviceKey(email)) ?? undefined;
      const res = await ocFetch<OcLoginSession | OcLoginCodeChallenge>("/tenant/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password, deviceToken, codeStep: true }),
      });
      if ("codeRequired" in res) {
        // A stale trusted-device token (expired, or the password changed) is
        // useless from here on; drop it so it isn't sent again.
        if (deviceToken) localStorage.removeItem(ocTrustedDeviceKey(email));
        setChallenge(res);
        setCode("");
        setNotice(null);
        setNow(Date.now());
        return;
      }
      completeLogin(res);
    } catch (err: unknown) {
      if (err instanceof OcApiError && err.status === 401) {
        setError("Invalid email or password.");
      } else {
        const body = err instanceof OcApiError ? (err.body as { error?: string } | undefined) : undefined;
        setError(body?.error ?? (err instanceof Error ? err.message : "Login failed"));
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    setError(null);
    setNotice(null);
    setLoading(true);
    try {
      const res = await ocFetch<OcLoginSession>("/tenant/auth/login/verify", {
        method: "POST",
        body: JSON.stringify({ challengeId: challenge.challengeId, code, trustDevice, factor: challenge.factor ?? "email" }),
      });
      completeLogin(res);
    } catch (err: unknown) {
      const body = err instanceof OcApiError ? (err.body as { error?: string; restart?: boolean } | undefined) : undefined;
      const msg = body?.error ?? (err instanceof Error ? err.message : "Verification failed");
      if (body?.restart) {
        backToPassword(msg);
      } else {
        setError(msg);
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!challenge || challenge.factor === "authenticator") return;
    setError(null);
    setNotice(null);
    try {
      const res = await ocFetch<{ expiresAt: number; resendAvailableAt: number }>("/tenant/auth/login/resend", {
        method: "POST",
        body: JSON.stringify({ challengeId: challenge.challengeId }),
      });
      setChallenge({ ...challenge, expiresAt: res.expiresAt, resendAvailableAt: res.resendAvailableAt });
      setCode("");
      setNotice(`We sent a new code to ${challenge.emailHint ?? "your email"}.`);
    } catch (err: unknown) {
      const body = err instanceof OcApiError ? (err.body as { error?: string; restart?: boolean } | undefined) : undefined;
      const msg = body?.error ?? (err instanceof Error ? err.message : "Could not resend the code");
      if (body?.restart) backToPassword(msg);
      else setError(msg);
    }
  }

  const resendIn = challenge?.resendAvailableAt
    ? Math.max(0, Math.ceil((challenge.resendAvailableAt - now) / 1000))
    : 0;
  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-oc-teal-500 focus:border-transparent";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4 [color-scheme:light]">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2.5 mb-3">
            <svg viewBox="0 0 48 54" fill="none" className="w-9 h-9 flex-shrink-0" aria-hidden="true">
              <path d="M24 2 L44 10 L44 26 C44 37 34 45 24 50 C14 45 4 37 4 26 L4 10 Z"
                    fill="none" stroke="#00D4FF" strokeWidth="2.5"/>
              <path d="M13 26 L20 33 L35 18" stroke="#00D4FF" strokeWidth="3.5"
                    strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="text-left">
              <div className="text-base font-bold text-slate-900 leading-tight">OnboardConnect</div>
              <div className="text-[10px] font-medium text-oc-teal-600 tracking-widest uppercase leading-tight">for Slate CRM</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">
            {!challenge
              ? "Sign in"
              : challenge.enroll
                ? "Set up your authenticator"
                : isAuthenticator
                  ? "Enter your code"
                  : "Check your email"}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Tenant Administration Portal</p>
        </div>

        {challenge ? (
          <form onSubmit={handleVerify} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
            {challenge.enroll ? (
              <div className="space-y-3">
                <p className="text-sm text-slate-600">
                  Your organization requires an authenticator app. Scan this with Microsoft
                  Authenticator, Google Authenticator, 1Password or any TOTP app, then enter the
                  6-digit code it shows.
                </p>
                {qrDataUrl && (
                  <div className="flex justify-center">
                    <img
                      src={qrDataUrl}
                      alt="QR code to add this account to your authenticator app"
                      className="rounded-lg border border-slate-200 bg-white p-1"
                      width={232}
                      height={232}
                    />
                  </div>
                )}
                {challenge.secret && (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3">
                    <div className="text-xs font-medium text-slate-500">
                      {qrDataUrl ? "Can't scan? Enter this key instead" : "Enter this key in your authenticator app"}
                    </div>
                    <div className="mt-1 font-mono text-sm tracking-wider text-slate-900 break-all select-all">
                      {challenge.secret}
                    </div>
                  </div>
                )}
                <p className="text-xs text-slate-500">
                  This key is shown once. Finish setting it up now — you will need it every time you
                  sign in.
                </p>
              </div>
            ) : isAuthenticator ? (
              <p className="text-sm text-slate-600">
                Enter the 6-digit code from your authenticator app.
              </p>
            ) : (
              <p className="text-sm text-slate-600">
                We emailed a 6-digit sign-in code to <span className="font-medium text-slate-900">{challenge.emailHint}</span>.
                It expires in 10 minutes.
              </p>
            )}
            {notice && !error && (
              <div className="rounded-lg bg-oc-teal-50 border border-oc-teal-200 px-4 py-3 text-sm text-slate-700" role="status">
                {notice}
              </div>
            )}
            {error && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" role="alert">
                {error}
              </div>
            )}

            <div className="space-y-1">
              <label htmlFor="oc-login-code" className="block text-sm font-medium text-slate-700">
                {isAuthenticator ? "Authenticator code" : "Sign-in code"}
              </label>
              <input
                id="oc-login-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                required
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className={`${inputCls} text-center text-lg tracking-[0.4em] font-mono`}
                placeholder="000000"
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={trustDevice}
                onChange={(e) => setTrustDevice(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-oc-teal-600 focus:ring-oc-teal-500"
              />
              <span className="text-sm text-slate-600">{"Don't ask for a code on this device for 30 days"}</span>
            </label>

            <button
              type="submit"
              disabled={loading || code.replace(/\s/g, "").length !== 6}
              className="w-full rounded-lg bg-oc-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-oc-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? "Verifying…" : "Verify and sign in"}
            </button>

            <div className="flex items-center justify-between text-xs">
              <button
                type="button"
                onClick={() => backToPassword(null)}
                className="font-medium text-slate-500 hover:text-slate-700"
              >
                ← Use a different account
              </button>
              {/* Nothing to resend for an authenticator — the app generates the code. */}
              {isAuthenticator ? (
                <span className="text-slate-400">Codes change every 30 seconds</span>
              ) : (
                <button
                  type="button"
                  onClick={handleResend}
                  disabled={resendIn > 0}
                  className="font-medium text-oc-teal-600 hover:text-oc-teal-700 disabled:text-slate-400 disabled:cursor-not-allowed"
                >
                  {resendIn > 0 ? `Resend code in ${resendIn}s` : "Resend code"}
                </button>
              )}
            </div>
          </form>
        ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 space-y-4">
          {expiredMessage && !error && (
            <div
              className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-700"
              role="status"
            >
              {expiredMessage}
            </div>
          )}
          {impersonationEnded && !error && (
            <div className="rounded-lg bg-oc-teal-50 border border-oc-teal-200 px-4 py-3 text-sm text-slate-700" role="status">
              Impersonation session ended. You can close this tab.
            </div>
          )}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-1">
            <label htmlFor="oc-login-email" className="block text-sm font-medium text-slate-700">Email</label>
            <input
              id="oc-login-email"
              type="email"
              required
              autoFocus={!email}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-oc-teal-500 focus:border-transparent"
              placeholder="you@institution.edu"
            />
          </div>

          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <label htmlFor="oc-login-password" className="block text-sm font-medium text-slate-700">Password</label>
              <Link
                to="/forgot-password"
                state={{ email }}
                className="text-xs font-medium text-oc-teal-600 hover:text-oc-teal-700"
              >
                Forgot password?
              </Link>
            </div>
            <input
              id="oc-login-password"
              type="password"
              required
              autoFocus={!!email}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-oc-teal-500 focus:border-transparent"
              placeholder="••••••••"
            />
          </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-oc-teal-600 focus:ring-oc-teal-500"
            />
            <span className="text-sm text-slate-600">Remember me</span>
          </label>

          <button
            type="submit"
            disabled={loading}
            className="w-full rounded-lg bg-oc-teal-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-oc-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        )}

      </div>
    </div>
  );
}
